// src/pages/Admin/ActaConsolidadaPreview.js
import React, { useMemo, useEffect, useState, useRef } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { HeaderPage } from "../../components/Admin";
import { Button, Icon, Message, Loader } from "semantic-ui-react";
import { ActaTemplateEditorConsolidado } from "../../components/Admin/ActaTemplateEditorConsolidado";
import { useAuth } from "../../hooks/useAuth";
import { getActaConsolidadaApi } from "../../api/meetings";
import { getMeetingsApi, updateMeetingApi } from "../../api/meeting";
import { getMeetingSessionsApi } from "../../api/meetingSession";
import logoSena from "../../assets/images/logo_sena.png";

// Utiles
function textToHtml(txt = "") {
  if (!txt) return "";
  const escaped = txt
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\n/g, "<br/>");
}
function htmlToText(html = "") {
  if (!html) return "";
  // elimina tags y normaliza espacios
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").replace(/\u00A0/g, " ");
  return text.trim();
}
function ensureStringArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => String(v ?? "").trim()).filter(Boolean);
}

export function ActaConsolidadaPreview() {
  const { state } = useLocation();
  const { meetingId } = useParams();
  const { auth } = useAuth();

  const [persisted, setPersisted] = useState(null);
  const [loadingPersisted, setLoadingPersisted] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Metadatos reunión
  const [meetingMeta, setMeetingMeta] = useState(null);

  // === ESTADO EDITABLE ===
  const [actaState, setActaState] = useState(null);
  const [compromisosState, setCompromisosState] = useState([]);
  const [asistentesState, setAsistentesState] = useState([]);
  const [saving, setSaving] = useState(false);

  // Cargar consolidado persistido si no viene en state
  useEffect(() => {
    let mounted = true;
    async function fetchPersisted() {
      if (state?.preview) return;
      if (!auth?.token) return;
      try {
        setError(null);
        setLoadingPersisted(true);
        const data = await getActaConsolidadaApi(meetingId, auth.token);
        if (!mounted) return;
        setPersisted(data);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "No fue posible cargar el acta consolidada");
      } finally {
        if (mounted) setLoadingPersisted(false);
      }
    }
    fetchPersisted();
    return () => {
      mounted = false;
    };
  }, [meetingId, auth?.token, state?.preview]);

  // Cargar metadatos de reunión + rango horario consolidado
  useEffect(() => {
    (async () => {
      if (!auth?.token) return;
      try {
        const meetings = await getMeetingsApi(auth.token);
        const meeting =
          (meetings || []).find((m) => String(m.id) === String(meetingId)) ||
          null;

        const sessions = await getMeetingSessionsApi(auth.token);
        const mine = (sessions || []).filter(
          (s) => String(s.meeting) === String(meetingId)
        );

        const starts = mine
          .map((s) => s.start_datetime)
          .filter(Boolean)
          .map((d) => new Date(d));
        const ends = mine
          .map((s) => s.end_datetime)
          .filter(Boolean)
          .map((d) => new Date(d));

        const startMin = starts.length ? new Date(Math.min(...starts)) : null;
        const endMax = ends.length ? new Date(Math.max(...ends)) : null;

        setMeetingMeta({
          title: meeting?.title || "",
          numero: meeting?.acta_numero || "",
          ciudad: meeting?.ciudad || "",
          lugar: meeting?.lugar || "",
          enlace: meeting?.enlace || "",
          centro: meeting?.centro || "",
          agenda: Array.isArray(meeting?.agenda) ? meeting.agenda : [],
          objetivos: Array.isArray(meeting?.objetivos) ? meeting.objetivos : [],
          fecha: startMin ? startMin.toLocaleDateString("es-CO") : "",
          hora_inicio: startMin
            ? startMin.toLocaleTimeString("es-CO", { hour12: true })
            : "",
          hora_fin: endMax
            ? endMax.toLocaleTimeString("es-CO", { hour12: true })
            : "",

          compromisos: Array.isArray(meeting?.compromisos_consolidados)
            ? meeting.compromisos_consolidados
            : [],
          asistentes: Array.isArray(meeting?.asistentes_consolidados)
            ? meeting.asistentes_consolidados
            : [],
        });
      } catch {
        setMeetingMeta(null);
      }
    })();
  }, [meetingId, auth?.token]);

  // Selección de datos preferidos
  const preview = state?.preview || persisted?.preview || null;
  const docxUrl = state?.docx_url || persisted?.docx_url || null;

  // Armar acta base
  const actaComputed = useMemo(() => {
    const p = preview || {};
    return {
      numero: meetingMeta?.numero || "",
      comite: meetingMeta?.title || "",
      ciudad: meetingMeta?.ciudad || "",
      fecha: meetingMeta?.fecha || "",
      hora_inicio: meetingMeta?.hora_inicio || "",
      hora_fin: meetingMeta?.hora_fin || "",
      lugar: meetingMeta?.lugar || "",
      enlace: meetingMeta?.enlace || "",
      centro: meetingMeta?.centro || "",
      agenda: meetingMeta?.agenda || [],
      objetivos: meetingMeta?.objetivos || [],
      desarrollo: textToHtml(p.desarrollo_text || ""),
      conclusiones:
        p.conclusiones_html || textToHtml(p.conclusiones_text || ""),
      logoUrl: logoSena,
    };
  }, [preview, meetingMeta]);

  // Inicializar estado editable
  useEffect(() => {
    if (!actaComputed) return;
    setActaState(actaComputed);
    setCompromisosState(meetingMeta?.compromisos || []);
    setAsistentesState(meetingMeta?.asistentes || []);
  }, [actaComputed]);

  // Handlers de edición (consolidado es editable en UI)
  const handleFieldChange = (field, value) =>
    setActaState((prev) => ({ ...(prev || {}), [field]: value }));
  const handleChangeCompromisos = (rows) => setCompromisosState(rows || []);
  const handleChangeAsistentes = (rows) => setAsistentesState(rows || []);

  // Refresh servidor
  const handleRefreshFromServer = async () => {
    if (!auth?.token) return;
    try {
      setRefreshing(true);
      const data = await getActaConsolidadaApi(meetingId, auth.token);
      setPersisted(data);
      setError(null);
    } catch (e) {
      setError(e?.message || "No fue posible actualizar el acta consolidada");
    } finally {
      setRefreshing(false);
    }
  };

  // Guardar Acta (consolidado) en Meeting
  const handleSave = async () => {
    if (!auth?.token || !actaState) return;
    try {
      setSaving(true);

      const normCompromiso = (c = {}) => ({
        actividad: String(c.actividad || "").trim(),
        fecha: String(c.fecha || "").trim(),
        responsable: String(c.responsable || "").trim(),
        firma: String(c.firma || "").trim(),
      });
      const normAsistente = (a = {}) => ({
        nombre: String(a.nombre || "").trim(),
        dependencia: String(a.dependencia || "").trim(),
        aprueba: String(a.aprueba || "").trim(),
        observacion: String(a.observacion || "").trim(),
        firma: String(a.firma || "").trim(),
      });

      const payload = {
        // metadatos
        title: actaState.comite || "",
        acta_numero: actaState.numero || "",
        ciudad: actaState.ciudad || "",
        lugar: actaState.lugar || "",
        enlace: actaState.enlace || "",
        centro: actaState.centro || "",
        agenda: ensureStringArray(actaState.agenda),
        objetivos: ensureStringArray(actaState.objetivos),
        // consolidado
        desarrollo_consolidado: htmlToText(actaState.desarrollo || ""),
        conclusiones_consolidadas_text: htmlToText(
          actaState.conclusiones || ""
        ),
        conclusiones_consolidadas_html: actaState.conclusiones || "",

        compromisos_consolidados: (compromisosState || [])
          .map(normCompromiso)
          .filter((c) => c.actividad), // al menos actividad
        asistentes_consolidados: (asistentesState || [])
          .map(normAsistente)
          .filter((a) => a.nombre), // al menos nombre
      };

      await updateMeetingApi(meetingId, payload, auth.token);
      alert("Acta consolidada guardada con éxito.");
    } catch (e) {
      console.error(e);
      alert(e?.message || "No fue posible guardar el acta consolidada.");
    } finally {
      setSaving(false);
    }
  };

  // Título pestaña
  useEffect(() => {
    const prev = document.title;
    document.title = `Vista consolidada — Reunión ${meetingId}`;
    return () => {
      document.title = prev;
    };
  }, [meetingId]);

  // Imprimir
  const printRef = useRef(null);
  const handlePrint = () => {
    if (!printRef.current) return;
    const html = printRef.current.innerHTML;
    const win = window.open("", "", "height=900,width=700");
    win.document.write(`
      <html>
        <head>
          <title></title>
          <style>${
            document.getElementById("print-styles-consolidado")?.innerHTML || ""
          }</style>
        </head>
        <body>${html}</body>
      </html>
    `);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
      win.close();
    }, 350);
  };

  const showEmpty = !preview && !loadingPersisted && !error;

  return (
    <>
      <HeaderPage title={`Vista previa consolidada — Reunión ${meetingId}`} />
      <div className="ui segment">
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <Button as={Link} to="/app/actas" icon labelPosition="left">
            <Icon name="arrow left" />
            Volver
          </Button>

          <Button primary icon labelPosition="left" onClick={handlePrint}>
            <Icon name="print" />
            Imprimir Acta
          </Button>

          {docxUrl ? (
            <Button
              as="a"
              href={docxUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="violet"
              icon
              labelPosition="left"
            >
              <Icon name="download" />
              Descargar Word
            </Button>
          ) : (
            <Button disabled icon labelPosition="left">
              <Icon name="download" />
              Word no disponible
            </Button>
          )}

          <Button
            color="grey"
            icon
            labelPosition="left"
            onClick={handleRefreshFromServer}
            loading={refreshing}
          >
            <Icon name="refresh" />
            Actualizar desde servidor
          </Button>

          {/* NUEVO: Guardar Acta */}
          <Button
            color="green"
            icon
            labelPosition="left"
            onClick={handleSave}
            loading={saving}
          >
            <Icon name="save" />
            Guardar Acta
          </Button>
        </div>

        {loadingPersisted && (
          <div style={{ padding: 12 }}>
            <Loader active inline="centered">
              Cargando acta consolidada...
            </Loader>
          </div>
        )}

        {!!error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {showEmpty && (
          <Message warning>
            <Message.Header>Sin vista previa disponible</Message.Header>
            <p>
              Genera el acta consolidada desde la tabla con{" "}
              <b>“Generar Acta (Auto)”</b> o verifica que ya exista una versión
              persistida.
            </p>
          </Message>
        )}

        {!!preview && actaState && (
          <div ref={printRef}>
            <ActaTemplateEditorConsolidado
              acta={actaState}
              compromisos={compromisosState}
              asistentes={asistentesState}
              onFieldChange={handleFieldChange}
              onChangeCompromisos={handleChangeCompromisos}
              onChangeAsistentes={handleChangeAsistentes}
              editable={true}
              meetingId={meetingId}
              token={auth?.token}
            />
          </div>
        )}

        {/* Estilos de impresión */}
        <style id="print-styles-consolidado">{`
          @media print { .no-print { display: none !important; } }
          .page-break { display: block; height: 32px; margin-bottom: 16px; page-break-after: always; }
          .acta-template { max-width: 900px; margin: 0 auto; background: #fff; padding: 32px 24px 40px; border-radius: 10px; font-size: 14px; }
          .acta-logo { display: block; margin: 0 auto 15px; max-width: 120px; }
          .acta-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 18px; }
          .acta-table th, .acta-table td { border: 1px solid #222; padding: 6px; vertical-align: top; text-align: left; }
          .acta-table th { font-weight: bold; background: #f5f7f6; text-align: center; }
          .acta-header { background: #e6f6ef; font-size: 17px; text-align: center; font-weight: bold; }
          .acta-section-header { background: #f6f7f9; font-weight: bold; text-align: center; font-size: 15px; }
          .annex-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 20px; margin-top: 20px; }
          .annex-item { margin: 0; text-align: center; }
          .annex-item img { width: 100%; height: auto; max-height: 300px; object-fit: cover; border: 1px solid #ddd; }
          .annex-item figcaption { margin-top: 8px; font-style: italic; font-size: 12px; color: #555; }
          .annex-empty { text-align: center; padding: 20px; color: #666; }
          .acta-editable { white-space: pre-wrap; word-break: break-word; line-height: 1.5; }
          .acta-editable ul { padding-left: 20px; }
          .acta-editable li { margin-bottom: 6px; }
        `}</style>
      </div>
    </>
  );
}

// // src/pages/Admin/ActaConsolidadaPreview.js
// import React, { useMemo, useEffect, useState, useRef } from "react";
// import { useLocation, useParams, Link } from "react-router-dom";
// import { HeaderPage } from "../../components/Admin";
// import { Button, Icon, Message, Loader } from "semantic-ui-react";
// import { ActaTemplateEditorConsolidado } from "../../components/Admin/ActaTemplateEditorConsolidado";
// import { useAuth } from "../../hooks/useAuth";
// import { getActaConsolidadaApi } from "../../api/meetings";
// import { getMeetingsApi } from "../../api/meeting";
// import { getMeetingSessionsApi } from "../../api/meetingSession";
// import logosena from "../../assets/images/logo_sena.png";

// // Util: texto plano -> HTML básico
// function textToHtml(txt = "") {
//   if (!txt) return "";
//   const escaped = txt
//     .replace(/&/g, "&amp;")
//     .replace(/</g, "&lt;")
//     .replace(/>/g, "&gt;");
//   return escaped.replace(/\n/g, "<br/>");
// }

// export function ActaConsolidadaPreview() {
//   const { state } = useLocation();
//   const { meetingId } = useParams();
//   const { auth } = useAuth();

//   const [persisted, setPersisted] = useState(null);
//   const [loadingPersisted, setLoadingPersisted] = useState(false);
//   const [error, setError] = useState(null);
//   const [refreshing, setRefreshing] = useState(false);

//   // Metadatos de reunión + rango de horas
//   const [meetingMeta, setMeetingMeta] = useState(null);

//   // === ESTADO LOCAL EDITABLE ===
//   const [actaState, setActaState] = useState(null);
//   const [compromisosState, setCompromisosState] = useState([]);
//   const [asistentesState, setAsistentesState] = useState([]);

//   // Cargar acto persistido si no vino por state
//   useEffect(() => {
//     let mounted = true;
//     async function fetchPersisted() {
//       if (state?.preview) return;
//       if (!auth?.token) return;
//       try {
//         setError(null);
//         setLoadingPersisted(true);
//         const data = await getActaConsolidadaApi(meetingId, auth.token);
//         if (!mounted) return;
//         setPersisted(data);
//       } catch (e) {
//         if (!mounted) return;
//         setError(e?.message || "No fue posible cargar el acta consolidada");
//       } finally {
//         if (mounted) setLoadingPersisted(false);
//       }
//     }
//     fetchPersisted();
//     return () => {
//       mounted = false;
//     };
//   }, [meetingId, auth?.token, state?.preview]);

//   // Cargar metadatos + rango de horas
//   useEffect(() => {
//     (async () => {
//       if (!auth?.token) return;
//       try {
//         const meetings = await getMeetingsApi(auth.token);
//         const meeting =
//           (meetings || []).find((m) => String(m.id) === String(meetingId)) ||
//           null;

//         const sessions = await getMeetingSessionsApi(auth.token);
//         const mine = (sessions || []).filter(
//           (s) => String(s.meeting) === String(meetingId)
//         );

//         const starts = mine
//           .map((s) => s.start_datetime)
//           .filter(Boolean)
//           .map((d) => new Date(d));
//         const ends = mine
//           .map((s) => s.end_datetime)
//           .filter(Boolean)
//           .map((d) => new Date(d));

//         const startMin = starts.length ? new Date(Math.min(...starts)) : null;
//         const endMax = ends.length ? new Date(Math.max(...ends)) : null;

//         setMeetingMeta({
//           title: meeting?.title || "",
//           numero: meeting?.acta_numero || "",
//           ciudad: meeting?.ciudad || "",
//           lugar: meeting?.lugar || "",
//           enlace: meeting?.enlace || "",
//           centro: meeting?.centro || "",
//           agenda: Array.isArray(meeting?.agenda) ? meeting.agenda : [],
//           objetivos: Array.isArray(meeting?.objetivos) ? meeting.objetivos : [],
//           fecha: startMin ? startMin.toLocaleDateString("es-CO") : "",
//           hora_inicio: startMin
//             ? startMin.toLocaleTimeString("es-CO", { hour12: true })
//             : "",
//           hora_fin: endMax
//             ? endMax.toLocaleTimeString("es-CO", { hour12: true })
//             : "",
//         });
//       } catch {
//         setMeetingMeta(null);
//       }
//     })();
//   }, [meetingId, auth?.token]);

//   // Preferir preview de navegación; si no, el persistido
//   const preview = state?.preview || persisted?.preview || null;
//   const docxUrl = state?.docx_url || persisted?.docx_url || null;

//   // Construir "acta" base cada vez que llega preview/metadatos
//   const actaComputed = useMemo(() => {
//     const p = preview || {};
//     return {
//       numero: meetingMeta?.numero || "",
//       comite: meetingMeta?.title || "",
//       ciudad: meetingMeta?.ciudad || "",
//       fecha: meetingMeta?.fecha || "",
//       hora_inicio: meetingMeta?.hora_inicio || "",
//       hora_fin: meetingMeta?.hora_fin || "",
//       lugar: meetingMeta?.lugar || "",
//       enlace: meetingMeta?.enlace || "",
//       centro: meetingMeta?.centro || "",
//       agenda: meetingMeta?.agenda || [],
//       objetivos: meetingMeta?.objetivos || [],
//       desarrollo: textToHtml(p.desarrollo_text || ""),
//       conclusiones:
//         p.conclusiones_html || textToHtml(p.conclusiones_text || ""),
//       logoUrl: logosena,
//     };
//   }, [preview, meetingMeta]);

//   // Inicializar estado editable cuando haya datos listos
//   useEffect(() => {
//     if (!actaComputed) return;
//     setActaState(actaComputed);
//     // Por ahora consolidado no trae compromisos/asistentes; quedan vacíos
//     setCompromisosState([]);
//     setAsistentesState([]);
//   }, [actaComputed]);

//   // Handlers que el editor necesita (¡antes venían vacíos!)
//   const handleFieldChange = (field, value) =>
//     setActaState((prev) => ({ ...(prev || {}), [field]: value }));

//   const handleChangeCompromisos = (rows) => setCompromisosState(rows || []);
//   const handleChangeAsistentes = (rows) => setAsistentesState(rows || []);

//   // Refresh servidor
//   const handleRefreshFromServer = async () => {
//     if (!auth?.token) return;
//     try {
//       setRefreshing(true);
//       const data = await getActaConsolidadaApi(meetingId, auth.token);
//       setPersisted(data);
//       setError(null);
//     } catch (e) {
//       setError(e?.message || "No fue posible actualizar el acta consolidada");
//     } finally {
//       setRefreshing(false);
//     }
//   };

//   // Título pestaña
//   useEffect(() => {
//     const prev = document.title;
//     document.title = `Vista consolidada — Reunión ${meetingId}`;
//     return () => {
//       document.title = prev;
//     };
//   }, [meetingId]);

//   // Imprimir
//   const printRef = useRef(null);
//   const handlePrint = () => {
//     if (!printRef.current) return;
//     const html = printRef.current.innerHTML;
//     const win = window.open("", "", "height=900,width=700");
//     win.document.write(`
//       <html>
//         <head>
//           <title></title>
//           <style>${
//             document.getElementById("print-styles-consolidado")?.innerHTML || ""
//           }</style>
//         </head>
//         <body>${html}</body>
//       </html>
//     `);
//     win.document.close();
//     setTimeout(() => {
//       win.focus();
//       win.print();
//       win.close();
//     }, 350);
//   };

//   const showEmpty = !preview && !loadingPersisted && !error;

//   return (
//     <>
//       <HeaderPage title={`Vista previa consolidada — Reunión ${meetingId}`} />
//       <div className="ui segment">
//         <div
//           style={{
//             display: "flex",
//             gap: 8,
//             marginBottom: 12,
//             flexWrap: "wrap",
//           }}
//         >
//           <Button as={Link} to="/actas" icon labelPosition="left">
//             <Icon name="arrow left" />
//             Volver
//           </Button>

//           <Button primary icon labelPosition="left" onClick={handlePrint}>
//             <Icon name="print" />
//             Imprimir Acta
//           </Button>

//           {docxUrl ? (
//             <Button
//               as="a"
//               href={docxUrl}
//               target="_blank"
//               rel="noopener noreferrer"
//               color="violet"
//               icon
//               labelPosition="left"
//             >
//               <Icon name="download" />
//               Descargar Word
//             </Button>
//           ) : (
//             <Button disabled icon labelPosition="left">
//               <Icon name="download" />
//               Word no disponible
//             </Button>
//           )}

//           <Button
//             color="grey"
//             icon
//             labelPosition="left"
//             onClick={handleRefreshFromServer}
//             loading={refreshing}
//           >
//             <Icon name="refresh" />
//             Actualizar desde servidor
//           </Button>
//         </div>

//         {loadingPersisted && (
//           <div style={{ padding: 12 }}>
//             <Loader active inline="centered">
//               Cargando acta consolidada...
//             </Loader>
//           </div>
//         )}

//         {!!error && (
//           <Message negative>
//             <Message.Header>Error</Message.Header>
//             <p>{error}</p>
//           </Message>
//         )}

//         {showEmpty && (
//           <Message warning>
//             <Message.Header>Sin vista previa disponible</Message.Header>
//             <p>
//               Genera el acta consolidada desde la tabla con{" "}
//               <b>“Generar Acta (Auto)”</b> o verifica que ya exista una versión
//               persistida.
//             </p>
//           </Message>
//         )}

//         {!!preview && actaState && (
//           <div ref={printRef}>
//             <ActaTemplateEditorConsolidado
//               acta={actaState}
//               compromisos={compromisosState}
//               asistentes={asistentesState}
//               onFieldChange={handleFieldChange}
//               onChangeCompromisos={handleChangeCompromisos}
//               onChangeAsistentes={handleChangeAsistentes}
//               editable={true} // ahora sí: los botones agregan filas/puntos
//               meetingId={meetingId}
//               token={auth?.token}
//             />
//           </div>
//         )}

//         {/* Estilos de impresión */}
//         <style id="print-styles-consolidado">{`
//           @media print { .no-print { display: none !important; } }
//           .page-break { display: block; height: 32px; margin-bottom: 16px; page-break-after: always; }
//           .acta-template { max-width: 900px; margin: 0 auto; background: #fff; padding: 32px 24px 40px; border-radius: 10px; font-size: 14px; }
//           .acta-logo { display: block; margin: 0 auto 15px; max-width: 120px; }
//           .acta-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 18px; }
//           .acta-table th, .acta-table td { border: 1px solid #222; padding: 6px; vertical-align: top; text-align: left; }
//           .acta-table th { font-weight: bold; background: #f5f7f6; text-align: center; }
//           .acta-header { background: #e6f6ef; font-size: 17px; text-align: center; font-weight: bold; }
//           .acta-section-header { background: #f6f7f9; font-weight: bold; text-align: center; font-size: 15px; }
//           .annex-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 20px; margin-top: 20px; }
//           .annex-item { margin: 0; text-align: center; }
//           .annex-item img { width: 100%; height: auto; max-height: 300px; object-fit: cover; border: 1px solid #ddd; }
//           .annex-item figcaption { margin-top: 8px; font-style: italic; font-size: 12px; color: #555; }
//           .annex-empty { text-align: center; padding: 20px; color: #666; }
//           .acta-editable { white-space: pre-wrap; word-break: break-word; line-height: 1.5; }
//           .acta-editable ul { padding-left: 20px; }
//           .acta-editable li { margin-bottom: 6px; }
//         `}</style>
//       </div>
//     </>
//   );
// }
